import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { GroupingTransferService } from '@shared/services/export-import-services/grouping';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { IGrouping, PalletisingService } from '@shared/services/palletising.service';
import { UserService } from '@shared/services/user.service';
import { actionDefintion } from '@shared/utils/SimpleActionManager';
import { catchError, forkJoin, Subject, takeUntil, throwError } from 'rxjs';
import { CreateUpdateGroupingDialogComponent } from './create-update-grouping/create-update-grouping';

interface IAction {
	title: string;
	handler: 'editGrouping' | 'deleteGrouping';
}

interface IRowAction {
	action: IAction;
	data: IGrouping;
}
@Component({
	selector: 'app-palletising-groupings',
	templateUrl: './groupings.component.html',
	styleUrls: ['./groupings.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class GroupingsComponent implements OnInit, OnDestroy {
	public isLoading: Subject<boolean> = new Subject<boolean>();
	public tableSource = new MatTableDataSource<IGrouping>();
	public groupingsFilter: string = '';
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();
	public unsubscribe: Subject<void> = new Subject<void>();

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'masterdata.palletising.name', dataKey: 'name' },
		{ name: 'masterdata.palletising.description', dataKey: 'description' },
		{ name: 'masterdata.palletising.type', dataKey: 'type' },
		{ name: 'masterdata.updatedDate', dataKey: 'updatedDate', sortKey: 'updatedAtEpoch' },
		{ name: 'masterdata.updatedBy', dataKey: 'updatedBy', sortAsShown: true },
	];

	public rowActions: actionDefintion<IGrouping>[] = [
		{
			title: 'masterdata.edit',
			handler: 'editGrouping',
		},
		{
			title: 'masterdata.delete',
			handler: 'deleteGrouping',
			disableFilter: (row) => !this.validationDataFetched || this.usedPalletTypes.includes(row.self),
			hideOnDisabled: false,
			filterDisableMessage: 'masterdata.palletising.grouping.delete.disabled.in-use',
		},
	];

	userNamesLookup: Map<string, string>;

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.palletising.newbtn.groupings',
			click: () => this.openCreateUpdateGrouping(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'masterdata.palletising.groupingImportExport.export.button',
			click: () => this.onExport(),
			permissions: () => ['palletising:PalletTypeList', 'palletising:PalletDimensionList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.palletising.groupingImportExport.import.button',
			click: () => this.onImport(),
			permissions: () => ['palletising:PalletTypePost', 'palletising:PalletTypePut', 'palletising:PalletDimensionPost', 'palletising:PalletDimensionPut', 'palletising:PalletTypeList', 'palletising:PalletDimensionList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.palletising.groupingImportExport.import.template.button',
			click: () => this.groupingTransferService.downloadTemplate(),
			permissions: () => ['palletising:PalletTypeList', 'palletising:PalletDimensionList'],
		},
	];

	public constructor(
		public dialog: MatDialog,
		private userService: UserService,
		private translator: TranslateService,
		private palletisingService: PalletisingService,
		private notificationService: NotificationService,
		private groupingTransferService: GroupingTransferService,
		private injector: Injector,
	) {}

	public ngOnInit(): void {
		this.loadData();

		this.loadValidationData();
	}

	private validationDataFetched: boolean = false;
	private usedPalletTypes: string[] = [];

	private loadValidationData(): void {
		this.validationDataFetched = false;
		forkJoin([this.palletisingService.fetchPallets(), this.palletisingService.fetchConfigurations()])
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: ([pallets, configurations]) => {
					const usedPalletTypesSet = new Set<string>();

					for (const config of configurations.items) {
						for (const combination of config.palletTypeCombinations) {
							if (combination.elementPalletTypes?.length === 0) continue;

							usedPalletTypesSet.add(combination.palletType.self);
							for (const element of combination.elementPalletTypes) {
								usedPalletTypesSet.add(element.palletType.self);
							}
						}
					}

					for (const pallet of pallets.items) {
						usedPalletTypesSet.add(pallet.type?.self);
						usedPalletTypesSet.add(pallet.dimension?.self);
					}

					this.usedPalletTypes = Array.from(usedPalletTypesSet);
					this.validationDataFetched = true;

					console.log('used pallet types:', this.usedPalletTypes);
				},
				error: () => {
					this.isLoading.next(false);
					this.notificationService.notify(['masterdata.palletising.errors.network'], [], 'error');
				},
			});
	}

	private loadData(): void {
		this.isLoading.next(true);

		this.userService
			.fetchUsers()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: (users) => {
					this.userNamesLookup = new Map(users.map((user) => [user.id, user.name]));
					this.updateGroupings(this.userNamesLookup);
					this.isLoading.next(false);
				},
				error: () => {
					this.isLoading.next(false);
					this.notificationService.notify(['masterdata.palletising.errors.network'], [], 'error');
				},
			});
	}

	public openCreateUpdateGrouping(data: IGrouping = null): void {
		this.dialog
			.open(CreateUpdateGroupingDialogComponent, {
				disableClose: true,
				width: '650px',
				data,
			})
			.componentInstance.saved.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	handleRowMenuAction(object: object): void {
		const row = object as IRowAction;
		if (row.action.handler === 'editGrouping') {
			this.openCreateUpdateGrouping(row.data);
		} else {
			this.onDeleteGrouping(row.data);
		}
	}

	onDeleteGrouping(grouping: IGrouping) {
		this.dialog
			.open(ConfirmdialogComponent, {
				width: '450px',
				data: {
					okReturnData: true,
					cancelReturnData: false,
					heading: this.translator.instant('masterdata.palletising.grouping.delete.heading'),
					content: this.translator.instant('masterdata.palletising.grouping.delete.content', { name: grouping.name }),
					okButtonText: this.translator.instant('general.ok'),
					cancelButtonText: this.translator.instant('general.cancel'),
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.palletisingService
						.deleteGrouping(grouping)
						.pipe(catchError(this.handleError))
						.subscribe({
							next: () => {
								this.notificationService.notify(['masterdata.palletising.grouping.notification.header'], ['masterdata.palletising.grouping.notification.content', { name: grouping.name }], 'success');
								this.loadData();
							},
							error: (error: Error) => {
								this.notificationService.notify([error.message], [], 'error');
							},
						});
				}
			});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			return throwError(() => new Error('masterdata.palletising.errors.network'));
		}
		return throwError(() => new Error('masterdata.palletising.errors.unknown'));
	}

	public onExport(): void {
		this.groupingTransferService
			.exportData(this.tableSource.data)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.palletising.groupingImportExport.export.notification.success.header'], ['masterdata.palletising.groupingImportExport.export.notification.success.content'], 'success');
				},
				error: (error: Error) => {
					console.error('Failed to export groupings', error);
					this.notificationService.notify(['masterdata.palletising.groupingImportExport.export.notification.error.header'], ['masterdata.palletising.groupingImportExport.export.notification.error.content'], 'error');
					this.dialog.open(TransferErrorPopupComponent, {
						width: '450px',
						data: { message: error.message },
					});
				},
			});
	}

	public onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.palletising.groupingImportExport.import.title',
				subHeadingKey: 'masterdata.palletising.groupingImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.palletising.groupingImportExport.import.button',
				notificationHeader: 'masterdata.palletising.groupingImportExport.import.notification.success.header',
				notificationContent: 'masterdata.palletising.groupingImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.palletising.groupingImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.palletising.groupingImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.palletising.groupingImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.palletising.groupingImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.groupingTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	public ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	updateGroupings(userNames: Map<string, string>): void {
		this.isLoading.next(true);
		this.palletisingService
			.loadGroupings(userNames)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: (value) => {
					this.countSubject$.next(value.length);
					this.tableSource.data = value;
					this.isLoading.next(false);
				},
				error: () => {
					this.notificationService.notify(['masterdata.palletising.errors.network'], [], 'error');
					this.isLoading.next(false);
				},
			});
	}
}
